<template>
    <div class="pt-4">
        <v-row class="fill-height">
            <v-col cols="12">
                <v-sheet>
                    <v-progress-linear
                        :style="{visibility: communicationsLoading ? 'visible' : 'hidden'}"
                        indeterminate
                        color="primary"
                    />
                    <client-only-schedule-x-calendar v-if="calendarApp" ref="calendar" :calendar-app="calendarApp">
                        <template v-slot:timeGridEvent="{calendarEvent}">
                            <div
                                class="event full-width full-height cursor-pointer"
                                style="height: max-content; border: 1px solid white; border-radius: 3px"
                                @click="showEvent($event, calendarEvent)"
                            >
                                <v-sheet
                                    :color="calendarEvent.color"
                                    class="v-event-draggable d-flex align-start px-2 full-width"
                                    style="padding-top: 2px; color: white"
                                >
                                    <v-icon size="x-small" style="margin-top: 3px" class="me-1">{{
                                        getCommunicationTypeIcon(calendarEvent)
                                    }}</v-icon>
                                    <span class="text-no-wrap">
                                        {{ formatEventTime(calendarEvent.start) }} -
                                        {{ formatEventTime(calendarEvent.end) }}
                                        <span class="font-weight-medium">
                                            {{ calendarEvent.title }}
                                        </span>
                                    </span>
                                </v-sheet>
                            </div>
                        </template>
                        <template v-slot:monthGridEvent="{calendarEvent}">
                            <div
                                class="event full-width full-height cursor-pointer"
                                @click="showEvent($event, calendarEvent)"
                            >
                                <v-sheet
                                    :color="calendarEvent.color"
                                    class="v-event-draggable d-flex align-center px-2 full-width"
                                    style="padding-top: 2px; color: white"
                                >
                                    <v-icon size="x-small" class="me-1">{{
                                        getCommunicationTypeIcon(calendarEvent)
                                    }}</v-icon>
                                    <span class="text-no-wrap">
                                        {{ formatEventTime(calendarEvent.start) }} -
                                        {{ formatEventTime(calendarEvent.end) }}
                                        <span class="font-weight-medium">
                                            {{ calendarEvent.title }}
                                        </span>
                                    </span>
                                </v-sheet>
                            </div>
                        </template>
                    </client-only-schedule-x-calendar>
                    <v-menu
                        v-model="selectedOpen"
                        location="end"
                        :close-on-content-click="false"
                        :activator="selectedElement"
                    >
                        <v-card color="grey-lighten-4" min-width="350px" flat>
                            <v-toolbar :color="selectedEvent.color" dark class="px-4">
                                <v-icon start>{{ getCommunicationTypeIcon(selectedEvent) }}</v-icon>
                                <v-toolbar-title class="text-no-wrap" v-html="selectedEvent.title" />
                                <v-spacer />
                            </v-toolbar>
                            <v-card-text v-if="selectedEvent.details">
                                <div v-if="selectedEvent.details.communicationType" class="d-flex">
                                    <span class="me-1 font-weight-medium"> {{ $t('type') }}: </span>
                                    <span>
                                        {{ $t(`communicationType.${selectedEvent.details.communicationType}`) }}
                                    </span>
                                </div>
                                <div v-if="selectedEvent.details.reservationNumber" class="d-flex">
                                    <span class="me-1 font-weight-medium"> {{ $t('order_number') }}: </span>
                                    <span>
                                        {{ selectedEvent.details.reservationNumber }}
                                    </span>
                                </div>
                                <div v-if="selectedEvent.details.startDate" class="d-flex">
                                    <span class="me-1 font-weight-medium"> {{ $t('start_date') }}: </span>
                                    <span>
                                        {{ selectedEvent.details.startDate }}
                                    </span>
                                </div>
                                <div v-if="selectedEvent.details.assignedUser" class="d-flex">
                                    <span class="me-1 font-weight-medium"> {{ $t('assigned_to') }}: </span>
                                    <span>
                                        {{ userName(selectedEvent.details.assignedUser) }}
                                    </span>
                                </div>
                            </v-card-text>
                            <v-card-actions>
                                <v-spacer />
                                <v-btn
                                    variant="text"
                                    color="secondary"
                                    @click="editCommunication(selectedEvent.details.communication)"
                                >
                                    {{ $t('edit') }}
                                </v-btn>
                                <v-btn variant="text" color="secondary" @click="selectedOpen = false">
                                    {{ $t('close') }}
                                </v-btn>
                            </v-card-actions>
                        </v-card>
                    </v-menu>
                </v-sheet>
            </v-col>
        </v-row>
    </div>
</template>

<script lang="ts">
    import {Component, Emit, Prop, Vue, Watch} from 'vue-facing-decorator'
    import {dateTimeFormat} from '@/utils/filters'
    import type {CommunicationType} from '~/utils/types/account/communications/types'
    import ClientOnlyScheduleXCalendar from '~/components/ClientOnlyScheduleXCalendar.vue'

    @Component({
        components: {
            ClientOnlyScheduleXCalendar,
        },
    })
    export class CommunicationsCalendar extends Vue {
        @Prop() assignedToId
        @Prop({default: false, type: Boolean}) communicationsLoading
        @Prop({default: false, type: Boolean}) createCommunicationDialog
        @Prop({default: []}) communications: CommunicationType[]

        selectedEvent = {}
        selectedElement = null
        selectedOpen = false
        calendarApp = null
        calendarScrollController = null

        @Watch('communications')
        onCommunicationsChanged() {
            const events = []

            this.communications?.forEach(communication => {
                const durationMinutes = 30
                const first = new Date(communication.communicationDateTime)
                const second = new Date(first.getTime() + durationMinutes * 60 * 1000)

                events.push({
                    id: communication.communicationId,
                    title: communication.subject,
                    start: this.$dateFns.format(first, 'yyyy-MM-dd HH:mm'),
                    end: this.$dateFns.format(second, 'yyyy-MM-dd HH:mm'),
                    color: this.getCommunicationTypeColor(communication),
                    details: {
                        reservationNumber: communication.reservationNumber,
                        assignedUser: communication.assignedUser,
                        communicationType: communication.communicationType,
                        communication: communication,
                        startDate: dateTimeFormat(first),
                    },
                })
            })

            this.calendarApp.events.set(events)
        }

        async mounted() {
            const {createCalendar, viewDay, viewWeek} = await import('@schedule-x/calendar')
            const {createScrollControllerPlugin} = await import('@schedule-x/scroll-controller')

            this.calendarScrollController = createScrollControllerPlugin({
                initialScroll: '08:00',
            })
            const isMobile = this.isMobile
            const calendarApp = createCalendar({
                views: isMobile ? [viewDay] : [viewWeek],
                defaultView: isMobile ? viewDay.name : viewWeek.name,
                firstDayOfWeek: this.$conf.public.firstDayOfWeek
                    ? this.$conf.public.firstDayOfWeek - 1
                    : this.firstDayOfWeekByLocale,
                datePicker: {
                    selectedDate: this.$dateFns.format(new Date()),
                },
                isDark: false,
                callbacks: {
                    onRangeUpdate: async () => {
                        await this.getCommunications()
                    },
                },
                events: [],
                calendars: {
                    leisure: {
                        colorName: 'leisure',
                        lightColors: {
                            main: '#1c7df9',
                            container: '#d2e7ff',
                            onContainer: '#002859',
                        },
                    },
                },
                plugins: [this.calendarScrollController],
                locale: this.$vuetify.date.locale,
            })
            this.calendarApp = shallowRef(calendarApp)

            await this.getCommunications()
        }

        @Emit()
        addCommunicationFilters(newFilters) {
            return newFilters
        }

        @Emit()
        async getCommunications() {
            this.addCommunicationFilters({
                communicationDateFrom: this.calendarApp.$app.calendarState.range.value.start.split(' ').join('T'),
                communicationDateTo: this.calendarApp.$app.calendarState.range.value.end.split(' ').join('T'),
                typeNEQ: 'NOTES',
            })
        }

        @Emit()
        async editCommunication(communication) {
            return communication
        }

        showEvent(event, calendarEvent) {
            const open = () => {
                this.selectedEvent = calendarEvent
                this.selectedElement = event.target
                requestAnimationFrame(() => requestAnimationFrame(() => (this.selectedOpen = true)))
            }

            if (this.selectedOpen) {
                this.selectedOpen = false
                requestAnimationFrame(() => requestAnimationFrame(() => open()))
            } else {
                open()
            }

            event.stopPropagation()
        }

        userName(user) {
            if (!user) return ''
            const {firstName, lastName} = user
            return `${firstName || ''} ${lastName || ''}`
        }

        rnd(a, b) {
            return Math.floor((b - a + 1) * Math.random()) + a
        }

        getCommunicationTypeIcon(communication) {
            if (!communication?.details) {
                return
            }

            switch (communication.details.communicationType) {
                case 'CALL':
                    return 'mdi-phone'
                case 'MEETING':
                    return 'mdi-account-group'
                case 'EMAIL':
                    return 'mdi-at'
                default:
                    return 'mdi-blur'
            }
        }

        getCommunicationTypeColor(communication) {
            switch (communication.communicationType) {
                case 'CALL':
                    return 'deep-purple'
                case 'MEETING':
                    return 'cyan'
                case 'EMAIL':
                    return 'green'
                default:
                    return 'orange'
            }
        }

        formatEventTime(date) {
            return new Date(date).toLocaleTimeString('en-US', {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            })
        }

        get firstDayOfWeekByLocale() {
            return this.$dateFns.localeOptions[this.$i18n.locale].weekStartsOn
        }

        get isMobile() {
            return this.$breakpoint.smAndDown
        }
    }

    export default CommunicationsCalendar
</script>

<style scoped lang="scss">
    :deep(.sx__calendar) {
        width: 100%;
    }

    :deep(.sx__view-selection) {
        display: none;
    }

    :deep(.sx-vue-calendar-wrapper) {
        height: 70vh;
    }
</style>
